import Vue from "vue";
import VueI18n from "vue-i18n";
import cookie from "js-cookie";

interface LanguageDetails {
  name: string;
  nativeName: string;
  alphabet?: string;
  single_case?: boolean;
  direction?: "ltr" | "rtl";
  numerals?: string;
  status?: { [app_id: string]: string };
  speech_to_text?: boolean;
  alphabet_range?: string[];
  alphabet_range_upper?: string[];
  numeral_range?: string[];
  large_character_set?: boolean;
  confusable_groups?: string[][];
  final_forms?: Record<string, string>;
  bad_words?: string[];
}


import raw_lang_codes from "../../data/lang_codes.json";

export type Lang = keyof typeof raw_lang_codes;

export type LangCodes = {
  [languageCode in Lang]: LanguageDetails;
};

export const lang_codes = raw_lang_codes as LangCodes;

const app_id = window.OpticalGymApp;

Vue.use(VueI18n);

export enum Locales {
  EN = "en",
  FR = "fr",
  ES = "es",
  HE = "he",
  AR = "ar",
  PT = "pt",
  EN_VISIONX = "en-visionx",
  EN_VEST = "en-vest",
  EN_HEALTHONE = "en-healthone",
}

export const LOCALES = [
  { value: Locales.EN, caption: "English" },
  { value: Locales.FR, caption: "Français" },
  { value: Locales.ES, caption: "Español" },
  { value: Locales.HE, caption: "עברית" },
  { value: Locales.AR, caption: "العربية" },
  { value: Locales.PT, caption: "Português (br)" },
  { value: Locales.EN_VISIONX, caption: "English (VisionX)" },
  { value: Locales.EN_VEST, caption: "English (vestibular.vision)" },
  { value: Locales.EN_HEALTHONE, caption: "English (HealthOne)" },
];

const en = require("./locales/en.yml");
const fr = require("./locales/fr.yml");
const es = require("./locales/es.yml");
const he = require("./locales/he.yml");
const pt = require("./locales/pt.yml");
const ar = require("./locales/ar.yml");
const en_visionx = require("./locales/en_visionx.yml");
const en_vest = require("./locales/en_vest.yml");
const en_healthone = require("./locales/en_healthone.yml");

export const messages = {
  [Locales.EN]: en,
  [Locales.FR]: fr,
  [Locales.ES]: es,
  [Locales.HE]: he,
  [Locales.PT]: pt,
  [Locales.AR]: ar,
  [Locales.EN_VISIONX]: en_visionx,
  [Locales.EN_VEST]: en_vest,
  [Locales.EN_HEALTHONE]: en_healthone,
};

// Determine default language - first check the URL "lang" query param, then for a cookie, then the browser's language
let urlParams = new URLSearchParams(window.location.search);
let selected_locale = urlParams.get("lang");
if (!selected_locale) {
  selected_locale = cookie.get("opticalgym_locale") || null;
}
if (!selected_locale) {
  selected_locale = "en";
  let full_locale = window.navigator.language;
  if (full_locale) {
    let short_locale = full_locale.substring(0, 2).toLowerCase();

    if (short_locale in lang_codes) {
      const short_lang = short_locale as Lang;
      if (lang_codes[short_lang]?.status?.[app_id] === "production") {
        selected_locale = short_lang;
      }
    }
  }
}

let lang = selected_locale.slice(0, 2).toLocaleLowerCase();
let locale = lang;

if (window.OpticalGymApp == "visionx") {
  locale += "-visionx";
}
if (window.OpticalGymApp == "vest") {
  locale += "-vest";
}
if (window.OpticalGymApp == "healthone") {
  locale += "-healthone";
}

// Extend the VueI18n class to add a lang() method, which is the language stripped of full locale info (e.g. 'en' from 'en-US', or 'fr' from 'fr-visionx')
export class OpticalgymVueI18n extends VueI18n {
  lang(): Lang {
    if (this.locale) {
      return this.locale.slice(0, 2) as Lang || "en";
    }
    return "en";
  }
}

let i18n = new OpticalgymVueI18n({
  locale: locale,
  fallbackLocale: "en",
  messages: messages,
  silentTranslationWarn: true,
});

// Set the HTML 'lang' attribute
document.documentElement.lang = i18n.lang();

export default i18n;
